import { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { getOrgTimezone, formatDateTime } from "../lib/timezone";
import { getEffectiveMembershipStatus } from "../lib/utils";
import { MembershipModal } from "../components/MembershipModal";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ClickableAvatar } from "../components/ClickableAvatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { DatePicker } from "../components/DatePicker";
import { AddPaymentModal } from "../components/AddPaymentModal";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Calendar, Package, CreditCard, DollarSign, Clock, User,
  History, Pencil, Trash2, Loader2, AlertTriangle, SplitSquareVertical, AlertCircle, Lock, ChevronDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembershipDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization, hasPermission } = useAuth();
  const orgTimezone = getOrgTimezone(organization);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (val) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", val);
    setSearchParams(nextParams);
  };

  const canAddPayment    = hasPermission("add_payment");
  const canEditPayment   = hasPermission("edit_payment");
  const canDeletePayment = hasPermission("delete_payment");
  const canRenew         = hasPermission("create_membership");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [packages, setPackages] = useState([]);

  const [editPayment, setEditPayment] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: 0, partialPayment: false, partialPaymentDate: "",
    paymentMode: "Cash", transactionId: "", notes: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [deletePayment, setDeletePayment] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expandedPaymentRows, setExpandedPaymentRows] = useState({});
  const togglePaymentRow = (id) => setExpandedPaymentRows(prev => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => { fetchData(); fetchPackages(); }, [id]);

  const fetchPackages = async () => {
    try { const res = await api.get("/packages"); setPackages(res.data.packages || []); } catch { }
  };

  const fetchData = async () => {
    try {
      const res = await api.get(`/memberships/${id}`);
      setData(res.data);
    } catch { toast.error("Membership not found"); navigate("/memberships"); }
    finally { setLoading(false); }
  };

  const openEdit = (p) => {
    const wasPartial = !!(p.partialPaymentDate || p.remaining > 0);
    setEditForm({
      amount: p.amount, partialPayment: wasPartial,
      partialPaymentDate: p.partialPaymentDate || "",
      paymentMode: p.paymentMode || "Cash",
      transactionId: p.transactionId || "",
      notes: p.notes || "",
    });
    setEditPayment(p);
  };

  const updateEdit = (key, val) => setEditForm(f => ({ ...f, [key]: val }));

  const handleEditSubmit = async () => {
    if (!editPayment) return;
    if (!editForm.amount || editForm.amount <= 0) { toast.error("Enter a valid amount"); return; }
    setEditLoading(true);
    try {
      await api.put(`/memberships/payments/${editPayment.id}`, {
        amount: editForm.amount,
        paymentMode: editForm.paymentMode,
        transactionId: editForm.transactionId,
        notes: editForm.notes,
        partialPaymentDate: editForm.partialPayment ? editForm.partialPaymentDate : "",
      });
      toast.success("Payment updated");
      setEditPayment(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update payment");
    } finally { setEditLoading(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!deletePayment) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/memberships/payments/${deletePayment.id}`);
      toast.success("Payment deleted");
      setDeletePayment(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete payment");
    } finally { setDeleteLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Loading</p>
      </div>
    </div>
  );

  if (!data) return null;

  const { membership, member, payments, package: pkg } = data;
  const lastPaymentId = payments?.[0]?.id;

  // Compute effective status using endDate (DB status never auto-updates to Expired)
  const effectiveStatus = getEffectiveMembershipStatus(membership, orgTimezone);

  const statusColor = {
    Active:   "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
    Partial:  "bg-amber-500/15 text-amber-500 border-amber-500/20",
    Expired:  "bg-red-500/15 text-red-500 border-red-500/20",
    Inactive: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
    Renewed:  "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  }[effectiveStatus] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";

  const editRemainingAfter = editPayment
    ? Math.max(0, (membership?.finalAmount || 0) - ((membership?.paidAmount || 0) - (editPayment.amount || 0) + (editForm.amount || 0)))
    : 0;
  const editBaseRemaining = editPayment
    ? Math.max(0, (membership?.finalAmount || 0) - ((membership?.paidAmount || 0) - (editPayment.amount || 0)))
    : 0;

  const renderPaymentDate = (dateStr) => {
    if (!dateStr) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="font-mono">
        {formatDateTime(dateStr, orgTimezone)}
      </span>
    );
  };

  const renderPaymentDetails = (p) => {
    const isLatest = p.id === lastPaymentId;
    return (
      <div className="text-sm text-foreground bg-secondary/5">
        <div className="flex items-center gap-2 p-2 border-b border-border/40 bg-secondary/10">
          {canEditPayment && (
            isLatest ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(p);
                }}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit Payment
              </Button>
            ) : (
              <span className="h-8 px-3 flex items-center gap-1.5 text-xs text-muted-foreground/30 bg-background/50 border border-border rounded cursor-not-allowed"
                title="Only the latest payment can be edited">
                <Lock className="h-3.5 w-3.5" /> Edit Locked
              </span>
            )
          )}
          {canDeletePayment && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors text-destructive hover:text-destructive border-red-500/20"
              onClick={(e) => {
                e.stopPropagation();
                setDeletePayment(p);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete Payment
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3">
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Payment Details</p>
            <div className="text-xs space-y-1 mt-1 text-muted-foreground">
              <p><span className="font-mono text-muted-foreground">Payment ID:</span> <span className="font-mono">{p.id}</span></p>
              <p><span className="font-mono text-muted-foreground">Transaction ID:</span> <span className="font-mono">{p.transactionId || "—"}</span></p>
              <p><span className="font-mono text-muted-foreground">Method/Mode:</span> <Badge variant="outline" className="text-[10px] py-0 px-1 font-mono">{p.paymentMode}</Badge></p>
              <p><span className="font-mono text-muted-foreground">Date:</span> <span className="font-mono">{formatDateTime(p.date, orgTimezone)}</span></p>
              <p><span className="font-mono text-muted-foreground">Remaining/Balance:</span> <span className="font-mono">₹{p.remaining?.toLocaleString()}</span></p>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Payment Notes</p>
            <p className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap mt-1">
              {p.notes || "No payment-specific notes provided."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const showActionsCol = canEditPayment || canDeletePayment;

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="membership-details-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/memberships")} className="rounded-xl">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">Membership Details</h1>
            <p className="text-sm text-muted-foreground">View subscription and payment details</p>
          </div>
        </div>
        {/* Add Payment — desktop header button */}
        {canAddPayment && (
          <Button onClick={() => setShowPaymentModal(true)}
            className="hidden sm:flex" data-testid="add-payment-btn">
            <CreditCard className="h-4 w-4 mr-2" />Add Payment
          </Button>
        )}
      </div>

      {/* Member Info Card */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="h-20 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" />
        <CardContent className="p-6 -mt-10">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <ClickableAvatar
                src={member?.photo}
                name={member?.name}
                className="h-20 w-20 border-4 border-background shadow-xl"
                fallbackClassName="bg-primary text-primary-foreground text-2xl font-heading"
              />
              <div className="pt-2">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <h2 className="text-2xl font-black font-heading">{member?.name}</h2>
                  <Badge className={`${statusColor} border text-xs font-mono uppercase w-fit`}>{effectiveStatus}</Badge>
                </div>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                  {member?.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{member.phone}</span>}
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <Button variant="link" className="h-auto p-0 text-primary text-sm" onClick={() => navigate(`/members/${member?.id}`)} data-testid="view-member-btn">
                    <User className="h-3.5 w-3.5 mr-1" /> View Member Profile
                  </Button>
                  {/* Add Payment inline button (mobile – below sm) */}
                  {canAddPayment && (
                    <Button size="sm" className="sm:hidden h-8 text-xs rounded-sm"
                      onClick={() => setShowPaymentModal(true)}>
                      <CreditCard className="h-3.5 w-3.5 mr-1" />Add Payment
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Amount Details in Card Header */}
            {membership && (
              <div className="flex flex-row items-center gap-5 sm:gap-8 bg-secondary/35 border border-border/40 rounded-2xl px-5 py-4 w-full md:w-auto shadow-sm select-none animate-in fade-in duration-200">
                <div className="flex flex-col">
                  <span className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">Final Amount</span>
                  <span className="text-lg md:text-xl font-black font-mono text-foreground mt-0.5">
                    ₹{membership.finalAmount?.toLocaleString()}
                  </span>
                </div>
                <div className="flex flex-col border-l border-border/40 pl-5 sm:pl-8">
                  <span className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">Paid Amount</span>
                  <span className="text-lg md:text-xl font-black font-mono text-emerald-500 mt-0.5">
                    ₹{membership.paidAmount?.toLocaleString()}
                  </span>
                </div>
                <div className="flex flex-col border-l border-border/40 pl-5 sm:pl-8">
                  <span className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">Balance Due</span>
                  <span className={`text-lg md:text-xl font-black font-mono mt-0.5 ${membership.remainingAmount > 0 ? "text-amber-500" : "text-emerald-500"}`}>
                    ₹{membership.remainingAmount?.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Expired Alert Banner */}
      {effectiveStatus === "Expired" && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-500/15 text-red-500 shrink-0">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-red-500">Membership Expired</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    This membership expired on {membership?.endDate?.slice(0, 10)}.
                    Renew to continue access.
                  </p>
                </div>
              </div>
              {canRenew && (
                <Button
                  size="sm"
                  className="font-heading uppercase tracking-wide text-xs gap-1.5 bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                  onClick={() => setShowRenewModal(true)}
                  data-testid="renew-membership-btn"
                >
                  <CreditCard className="h-3.5 w-3.5" /> Renew Membership
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-2 md:space-y-4">
        <TabsList className="bg-secondary/50 p-1">
          <TabsTrigger value="overview" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-overview">
            <Package className="h-3.5 w-3.5 mr-2" />Overview
          </TabsTrigger>
          <TabsTrigger value="payments" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-payments">
            <History className="h-3.5 w-3.5 mr-2" />Payment History
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" /> Package Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Package</span>
                  <span className="font-medium">{pkg?.name || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span>{pkg?.durationMonths || 0} months {pkg?.durationDays || 0} days</span>
                </div>
                <div className="pt-3 border-t border-border space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Start Date</span>
                    <span className="font-mono">{membership?.startDate}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> End Date</span>
                    <span className="font-mono">{membership?.endDate}</span>
                  </div>
                  {(membership?.renewedFromPackageName || membership?.renewedToPackageName) && (
                    <div className="pt-3 border-t border-border space-y-2">
                      {membership.renewedFromPackageName && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground flex items-center gap-1.5">🔄 Renewed From</span>
                          <span className="font-mono bg-secondary/50 px-2 py-0.5 rounded cursor-pointer text-primary hover:underline" onClick={() => navigate(`/memberships/${membership.renewedFromMembershipId}`)}>
                            {membership.renewedFromPackageName}
                          </span>
                        </div>
                      )}
                      {membership.renewedToPackageName && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground flex items-center gap-1.5">➡️ Renewed To</span>
                          <span className="font-mono bg-secondary/50 px-2 py-0.5 rounded cursor-pointer text-primary hover:underline" onClick={() => navigate(`/memberships/${membership.renewedToMembershipId}`)}>
                            {membership.renewedToPackageName}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-emerald-500" /> Payment Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Original Amount</span>
                  <span className="font-mono">₹{membership?.amount?.toLocaleString()}</span>
                </div>
                {membership?.discountValue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Discount ({membership.discountType === "percentage"
                        ? `${membership.discountValue}%`
                        : `₹${membership.discountValue}`})
                    </span>
                    <span className="text-emerald-500 font-mono">-₹{(membership.amount - membership.finalAmount).toLocaleString()}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-border space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Final Amount</span>
                    <span className="font-mono font-bold">₹{membership?.finalAmount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Paid Amount</span>
                    <span className="font-mono text-emerald-500">₹{membership?.paidAmount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Remaining</span>
                    <span className={`font-mono font-bold ${membership?.remainingAmount > 0 ? "text-amber-500" : "text-emerald-500"}`}>
                      ₹{membership?.remainingAmount?.toLocaleString()}
                    </span>
                  </div>
                  {membership?.partialPaymentDate && (
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-amber-500" /> Next Payment Due
                      </span>
                      <span className="font-mono text-amber-500 font-semibold">{membership.partialPaymentDate.slice(0, 10)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="payments">
          <Card className="bg-card border-border overflow-hidden flex flex-col" style={{ minHeight: "400px" }}>
            <CardHeader className="flex flex-row items-center justify-between py-2 px-4 border-b">
              <CardTitle className="text-sm font-heading uppercase tracking-wide">Payment History</CardTitle>
              {/* Add Payment button inside payments tab header for quick access */}
              {canAddPayment && (
                <Button size="sm" className="rounded-sm text-xs"
                  onClick={() => setShowPaymentModal(true)} data-testid="add-payment-tab-btn">
                  <CreditCard className="h-3.5 w-3.5 mr-1" />Add Payment
                </Button>
              )}
            </CardHeader>

            {/* ── Desktop table (hidden below md) ── */}
            <div className="hidden md:block overflow-auto flex-1 animate-in fade-in duration-200">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Transaction</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No payments recorded
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments?.map((p) => (
                      <Fragment key={p.id}>
                        <TableRow className="group cursor-pointer hover:bg-secondary/50 border-b border-border" onClick={() => togglePaymentRow(p.id)}>
                          <TableCell className="w-10">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); togglePaymentRow(p.id); }}>
                              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedPaymentRows[p.id] ? "rotate-180" : ""}`} />
                            </Button>
                          </TableCell>
                          <TableCell>{renderPaymentDate(p.date)}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-sm font-medium">
                              <Package className="h-3.5 w-3.5 text-primary shrink-0" />
                              {pkg?.name || "Membership Payment"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{p.paymentMode}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{p.transactionId || "-"}</TableCell>
                          <TableCell className="font-mono font-bold text-emerald-500">₹{p.amount?.toLocaleString()}</TableCell>
                          <TableCell className="max-w-[180px]">
                            {p.notes ? (
                              <span className="text-sm text-muted-foreground truncate block" title={p.notes}>
                                {p.notes}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedPaymentRows[p.id] && (
                          <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                            <TableCell colSpan={7} className="p-0">
                              {renderPaymentDetails(p)}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* ── Mobile Card List (hidden on md+) ── */}
            <div className="md:hidden overflow-auto flex-1 divide-y divide-border">
              {payments?.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground text-sm">No payments recorded</p>
              ) : (
                payments?.map((p, idx) => (
                  <Collapsible
                    key={p.id}
                    open={expandedPaymentRows[p.id]}
                    onOpenChange={() => togglePaymentRow(p.id)}
                    asChild
                  >
                    <div className={` ${idx !== payments.length - 1 ? "" : "border-b border-border"} hover:bg-secondary/10 transition-colors`}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-3 py-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="flex items-center justify-between flex-col shrink-0">
                              <Avatar className="h-6 w-6 border border-border">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">
                                  <DollarSign className="h-3 w-3 text-emerald-500" />
                                </AvatarFallback>
                              </Avatar>
                              <Button variant="ghost" size="icon" className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                                <ChevronDown className={`h-3 w-3 size-3 transition-transform duration-200 ${expandedPaymentRows[p.id] ? "rotate-180" : ""}`} />
                              </Button>
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm leading-tight truncate">
                                {pkg?.name || "Membership Payment"}
                              </p>
                              <p className="text-[11px] text-muted-foreground font-mono mt-1">
                                {formatDateTime(p.date, orgTimezone)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="flex items-end flex-col gap-0.5 text-right">
                              <Badge variant="outline" className="text-[9px] font-mono uppercase py-0 px-1.5">
                                {p.paymentMode}
                              </Badge>
                              <div className="mt-1 leading-none font-mono">
                                <p className="font-bold text-sm text-emerald-500">
                                  ₹{p.amount?.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-border/50 bg-secondary/5 animate-in fade-in slide-in-from-top-1 duration-200">
                          {renderPaymentDetails(p)}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Sticky mobile FAB for Add Payment ── */}
      {canAddPayment && (
        <div className="sm:hidden fixed bottom-6 left-0 right-0 px-4 z-50">
          <Button className="w-full rounded-xl shadow-lg h-12 text-sm font-heading uppercase tracking-wide"
            onClick={() => setShowPaymentModal(true)} data-testid="add-payment-fab">
            <CreditCard className="h-4 w-4 mr-2" />Add Payment
          </Button>
        </div>
      )}

      {/* Add Payment Modal */}
      <AddPaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        membership={membership}
        packageName={pkg?.name || ""}
        onSuccess={fetchData}
      />

      {/* Edit Payment Dialog */}
      <Dialog open={!!editPayment} onOpenChange={(v) => { if (!v && !editLoading) setEditPayment(null); }}>
        <DialogContent className="bg-card border-border max-w-md p-0 flex flex-col max-h-[90vh]">
          <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
            <DialogTitle className="font-heading text-xl uppercase tracking-wide">Edit Payment</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 relative">
            {editLoading && (
              <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[2px] flex items-center justify-center rounded-b-xl">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="font-mono">Saving...</span>
                </div>
              </div>
            )}
            <div className={`flex flex-col gap-5 ${editLoading ? "pointer-events-none select-none" : ""}`}>
              <div className="flex items-center gap-3 p-4 bg-secondary/30 rounded-xl">
                <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <Package className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{pkg?.name || "—"}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {membership?.startDate?.slice(0, 10)} → {membership?.endDate?.slice(0, 10)}
                  </p>
                </div>
                <Badge className="ml-auto text-[10px] font-mono uppercase border" style={{ background: "transparent" }}>
                  {membership?.status}
                </Badge>
              </div>
              <Card className="bg-amber-500/5 border-amber-500/20">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                      Balance (excl. this payment)
                    </span>
                    <span className="text-xl font-black font-mono text-amber-500">₹{editBaseRemaining.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className={`border transition-colors ${editForm.partialPayment ? "border-amber-500/30 bg-amber-500/5" : "border-border"}`}>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        editForm.partialPayment ? "bg-amber-500/15 text-amber-500" : "bg-secondary text-muted-foreground"
                      }`}>
                        <SplitSquareVertical className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Partial Payment</p>
                        <p className="text-xs text-muted-foreground">Pay a portion of the balance</p>
                      </div>
                    </div>
                    <Switch checked={editForm.partialPayment} onCheckedChange={(v) => updateEdit("partialPayment", v)} disabled={editLoading} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                      {editForm.partialPayment ? "Paid Amount (₹)" : "Amount (₹)"}
                    </Label>
                    <Input type="number" value={editForm.amount}
                      onChange={(e) => updateEdit("amount", parseFloat(e.target.value) || 0)}
                      disabled={editLoading} />
                  </div>
                  <div className={`flex justify-between items-center py-2 px-3 rounded-lg ${
                    editForm.partialPayment ? "bg-amber-500/10" : "bg-emerald-500/10"
                  }`}>
                    <span className={`text-sm font-medium ${editForm.partialPayment ? "text-amber-500" : "text-emerald-500"}`}>
                      {editForm.partialPayment ? "Still Remaining" : "Remaining after save"}
                    </span>
                    <span className={`text-lg font-bold font-mono ${editForm.partialPayment ? "text-amber-500" : "text-emerald-500"}`}>
                      ₹{editRemainingAfter.toLocaleString()}
                    </span>
                  </div>
                  {editForm.partialPayment && (
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Next Payment Due Date</Label>
                      <DatePicker value={editForm.partialPaymentDate} onChange={(v) => updateEdit("partialPaymentDate", v)}
                        placeholder="Select due date" disabled={editLoading} />
                    </div>
                  )}
                </CardContent>
              </Card>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <CreditCard className="h-3.5 w-3.5" /> Payment Mode
                  </Label>
                  <Select value={editForm.paymentMode} onValueChange={(v) => updateEdit("paymentMode", v)} disabled={editLoading}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Transaction ID</Label>
                  <Input value={editForm.transactionId} onChange={(e) => updateEdit("transactionId", e.target.value)}
                    placeholder="Optional" disabled={editLoading} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => updateEdit("notes", e.target.value)}
                  rows={2} placeholder="Add any notes..." disabled={editLoading} />
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-secondary/20 gap-2 shrink-0">
            <Button variant="outline" onClick={() => setEditPayment(null)} disabled={editLoading}>Cancel</Button>
            <Button onClick={handleEditSubmit} disabled={editLoading} className="font-heading uppercase tracking-wider">
              {editLoading
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                : `Save Changes • ₹${(editForm.amount || 0).toLocaleString()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Dialog */}
      <Dialog open={!!deletePayment} onOpenChange={(v) => { if (!v) setDeletePayment(null); }}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wide flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" /> Delete Payment
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete this payment? This action cannot be undone and the membership balance will be recalculated.
            </p>
            {deletePayment && (
              <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono text-emerald-500">₹{deletePayment.amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mode</span>
                  <span>{deletePayment.paymentMode}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-mono">{deletePayment.date?.slice(0, 10)}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletePayment(null)} disabled={deleteLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleteLoading}
              className="font-heading uppercase tracking-wider">
              {deleteLoading
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Deleting...</>
                : "Delete Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MembershipModal
        open={showRenewModal}
        onOpenChange={(o) => { if (!o) setShowRenewModal(false); }}
        member={member}
        membership={membership}
        isRenewal={true}
        packages={packages.filter(p => p.status === "Active")}
        branchId={membership?.branchId}
        onSuccess={fetchData}
      />
    </div>
  );
}
